import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref as dbRef, onValue } from "firebase/database";
import "./OperationBlock.css";

// Nodes in RTDB:
const MESSAGES_NODE = "message";
const CODE_STREAM_NODE = "code_stream";

export default function OperationBlock({ database, blockId, title = "Operation" }) {
  const [messages, setMessages] = useState([]); // [{ts, text}]
  const [codeText, setCodeText] = useState(""); // string (or reconstructed from object)
  const codeBoxRef = useRef(null);

  const basePath = useMemo(() => `model_builders/blocks/${blockId}`, [blockId]);

  useEffect(() => {
    if (!database || !blockId) return;

    const msgRef = dbRef(database, `${basePath}/${MESSAGES_NODE}`);
    const csRef = dbRef(database, `${basePath}/${CODE_STREAM_NODE}`);

    const unsubMsgs = onValue(msgRef, (snap) => {
      const val = snap.val();

      if (!val || typeof val !== "object") {
        setMessages([]);
        return;
      }

      const next = Object.entries(val)
        .map(([k, v]) => ({
          ts: Number.isFinite(Number(k)) ? Number(k) : Date.now(),
          text: String(v ?? ""),
        }))
        .sort((a, b) => a.ts - b.ts);

      setMessages(next);
    });

    const unsubCS = onValue(csRef, (snap) => {
      const val = snap.val();

      if (val == null) {
        setCodeText("");
        return;
      }

      if (typeof val === "string") {
        setCodeText(val);
        return;
      }

      if (typeof val === "object") {
        const text = Object.entries(val)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([, v]) => String(v ?? ""))
          .join("\n");
        setCodeText(text);
        return;
      }

      setCodeText(String(val));
    });

    return () => {
      unsubMsgs();
      unsubCS();
    };
  }, [database, blockId, basePath]);

  // Optional: auto-scroll code to bottom when it updates
  useEffect(() => {
    const el = codeBoxRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [codeText]);

  return (
    <div className="op-card">
      <div className="op-header">
        <div className="op-title">{title}</div>
        <div className="op-meta">
          id: <code>{blockId}</code>
        </div>
      </div>

      {/* Messages (styled like Instructions) */}
      {messages.length > 0 && (
        <div className="op-instructions">
          <strong>Updates:</strong>
          <div style={{ marginTop: 6 }}>
            {messages.map((m) => `• ${m.text}`).join("\n")}
          </div>
        </div>
      )}

      {/* Code stream */}
      {codeText? 
      <div>
        <div className="op-section-title">Code Output</div>
        <div ref={codeBoxRef} className="op-code-box">
            {codeText}
        </div>
      </div>
      :
      null
    }
    </div>
  );
}
